/* eslint-disable @nx/enforce-module-boundaries */
import { DI } from '@intigral/constant/config/DIContainer';
import { IGetChannelScheduleResponse } from '@intigral/domain';
import { container } from '@intigral/domain/lib/DataDomain';
import { IChannelService } from '@intigral/domain/lib/application/services/ChannelService';
import { convertFilterParamsToObject } from '@intigral/utils/lib/helpers/string';
import { getSubscriptionGuids } from '@intigral/utils/lib/user';
import to from 'await-to-js';
import _ from 'lodash';
import { getConfiguration } from '../../hooks/useConfiguration';
import moment from 'moment';
import { appConfig } from '../../config/app.config';
import channelsUtil from '../../helpers/channelsUtil';
import h, { appData, getCountry } from '../../helpers/common';
import user from '../../helpers/user';
import { transformMiniEPGItem } from '../../utils/transformResponse/jawwyResponse';
import { IUserRegistrationService } from '@intigral/domain/lib/application/services/UserRegistrationService';

export const getUserChannelsApi = async (params, ppvEventConfig = []) => {
    const [err, channels] = await to(getChannelsApi(params));
    if (err) {
        return [];
    }
    return updateChannelDataToUtils(channels, ppvEventConfig);
};

export const getUserAllChannelsApi = async (params, ppvEventConfig = []) => {
    const [err, channels] = await to(getAllChannelsApi(params));
    if (err) {
        return [];
    }
    return updateChannelDataToUtils(channels, ppvEventConfig);
};

export const updateChannelDataToUtils = async (channels, ppvEventConfig = []) => {
    // Use ppvEventConfig from Redux, do NOT fetch it here
    const ppvEvents = ppvEventConfig.flatMap((country) =>
        country.paymentMethods.flatMap((paymentMethod) => paymentMethod.events)
    );

    const enrichedChannels = channels.map((channel) => {
        let hasPPV = false;
        if (Array.isArray(channel.subscriptions)) {
            for (const sub of channel.subscriptions) {
                for (const event of ppvEvents) {
                    if (sub.subscriptionGUID === event.subscriptionGuid) {
                        hasPPV = true;
                        break;
                    }
                }
                if (hasPPV) break;
            }
        }
        return { ...channel, isPPV: !!hasPPV };
    });
    channelsUtil.setUserChannels(enrichedChannels);
    // for genres
    const genresObj = enrichedChannels
        .map((channel) => [...channel.category])
        .flat()
        .reduce((acc, value, index) => ({ ...acc, [value]: (acc[value] || 0) + 1, All: index + 1 }), {});
    // Initial favorite channels count
    genresObj['Favorite'] = 0;
    const genresArray = Object.keys(genresObj)
        .sort()
        .map((genreKey) => {
            return {
                Id: genreKey,
                type: 'text',
                label: `channel_filter_${genreKey.toLowerCase()}_label`,
                count: genresObj[genreKey],
                value: genreKey,
            };
        });
    return { channels: enrichedChannels, genres: genresArray };
};
export const getChannelsApi = async (params: any = {}) => {
    const userInfo = user.getUserInfo();
    //  FIXED: Using React Query configuration instead of appData
    const config = getConfiguration();
    const subscriptionGuids = getSubscriptionGuids('epg', userInfo, config?.paidVsFree || {})
        .sort()
        .join('|');

    let searchParams = {
        apiKey: user.getAPIKey(),
        username: user.getUsername(),
        country: getCountry(),
        deviceType: h.getDeviceType(),
        tierGuid: user.getUserActiveTierGuid(),
        filter: `subscriptionGuids:${subscriptionGuids}`,
        device: h.getDevice(),
    };

    if (params?.filter_params) {
        searchParams = {
            ...searchParams,
            ...convertFilterParamsToObject(params.filter_params), // conevrt string to object key value
        };
    }
    if (user.getProfileMode() === 'kid') {
        // get kids channels
        searchParams['categoryType'] = 'kids';
    }
    const channelService = container.resolve<IChannelService>(DI.SERVICES.IChannelService);
    return channelService.getChannels(searchParams).catch((_error) => {
        if (_error) {
            return [];
        }
    });
};

export const getAllChannelsApi = async (params: any = {}) => {
    let searchParams = {
        apiKey: user.getAPIKey(),
        username: user.getUsername(),
        country: getCountry(),
        deviceType: h.getDeviceType(),
        tierGuid: user.getUserActiveTierGuid(),
        filter: '',
        device: h.getDevice(),
    };

    if (params?.filter_params) {
        searchParams = {
            ...searchParams,
            ...convertFilterParamsToObject(params.filter_params), // conevrt string to object key value
        };
    }
    if (user.getProfileMode() === 'kid') {
        // get kids channels
        searchParams['categoryType'] = 'kids';
    }
    const channelService = container.resolve<IChannelService>(DI.SERVICES.IChannelService);

    const response = await channelService.getAllChannels(searchParams).catch((_error) => {
        return _error;
    });

    return response;
};

export const getChannelSchedule = (byId = '', date) => {
    const channelService = container.resolve<IChannelService>(DI.SERVICES.IChannelService);

    const searchParams = {
        timeZoneOffset: String(channelsUtil.getTimeZoneOffset()),
        productKey: appConfig.productKey,
        date: date,
        byId: byId,
    };
    return channelService
        .getChannelSchedule(searchParams)
        .then((response) => {
            return (response && response[0]) || '';
        })
        .catch((_error) => {
            return _error;
        });
};

export const getMiniEPGApi = async (userChannels) => {
    if (userChannels && userChannels.length) {
        try {
            const programsSchedules = await Promise.all(
                _.map(userChannels, (ch) => getChannelSchedulesApi(ch.channelID))
            );
            const miniEPG = _.map(userChannels, (channel) => {
                return {
                    ...channel,
                    programs: _.map(
                        (_.find(programsSchedules, (p: any) => p.channelId === channel.channelID) || {}).listings,
                        (item) => transformMiniEPGItem(item, channel)
                    ),
                };
            });
            return miniEPG;
        } catch (error) {
            return [];
        }
    } else {
        return [];
    }
};

export const getChannelSchedulesApi = async (byId) => {
    const epgDates = channelsUtil.getEPGDays();
    const promises = [];
    let schedules: any = {};
    for (let i = 0; i < epgDates.length; i++) {
        const p = getChannelSchedule(byId, epgDates[i]);
        promises.push(p);
    }
    for (const promise of promises) {
        const [err, response] = await to<IGetChannelScheduleResponse[0]>(promise);
        if (err) {
            throw new Error('[ChannelService] ChannelSchedule fetch failed!');
        }
        if (response) {
            schedules.channelId = response.channelId;
            if (schedules.listings) {
                schedules.listings = [...schedules.listings, ...response.listings];
            } else {
                schedules.listings = response.listings;
            }
        }
    }
    //Return listings based on BufferEnd +- values
    const [start, end] = channelsUtil.getEPGBufferEnds();
    schedules = Object.assign({}, schedules, {
        listings: channelsUtil.filterChannelListingsByBufferEnds(schedules.listings, start, end),
    });
    return schedules;
};

export const getChannelScheduleByDataSource = async (datasource: string) => {
    const channelService = container.resolve<IChannelService>(DI.SERVICES.IChannelService);
    const searchParams = {
        offset: String(channelsUtil.getTimeZoneOffset()),
        date: moment().format('YYYY-MM-DD'),
    };
    const response = await channelService.getChannelScheduleByDataSource(datasource, searchParams);
    return response?.[0] || null;
};
